import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import { publicAPI } from "../services/api";
import placeimage from "../image/noimage.jpg";

export default function TrainerPublicProfile() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [trainer, setTrainer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("about");

    useEffect(() => {
        loadTrainerProfile();
    }, [slug]);

    const loadTrainerProfile = async () => {
        setLoading(true);
        try {
            const response = await publicAPI.getTrainerBySlug(slug);
            if (response.data?.success) {
                setTrainer(response.data.data);
            } else {
                toast.error("Trainer not found");
                navigate("/gym-fitness-trainer");
            }
        } catch (error) {
            if (error.response?.status === 404) {
                toast.error("Trainer not found");
                navigate("/gym-fitness-trainer");
            } else {
                toast.error("Failed to load trainer profile");
            }
        } finally {
            setLoading(false);
        }
    };

    const renderStars = (rating) => {
        return [...Array(5)].map((_, index) => (
            <i
                key={index}
                className={`bi ${index < rating ? "bi-star-fill star-filled" : "bi-star star-empty"}`}
            ></i>
        ));
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner-border text-danger" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!trainer) {
        return (
            <div className="error-container">
                <i className="bi bi-exclamation-triangle error-icon-large"></i>
                <h3 className="mt-3">Trainer Not Found</h3>
                <p className="text-muted">The trainer you're looking for doesn't exist or has been removed.</p>
                <Link className="btn btn-danger mt-3" to="/gym-fitness-trainer">
                    Browse Trainers
                </Link>
            </div>
        );
    }

    const trainerName = trainer.name || "";
    const trainerCity = trainer.city || "India";
    const trainerExperience = trainer.experience || 0;
    const specializationNames = (trainer.specializations || []).map(s => s.name).join(", ");
    const trainerBio = trainer.about || trainer.sort_about || "";

    return (
        <>
            <Helmet>
                <title>{trainerName} - Certified Fitness Trainer | MeraGym</title>
                <meta
                    name="description"
                    content={`${trainerName} is a certified fitness trainer in ${trainerCity} with ${trainerExperience} years of experience. ${specializationNames ? `Specializing in ${specializationNames}.` : ""} Book your session now!`}
                />
                <meta name="keywords" content={`fitness trainer, personal trainer, ${trainerCity}, ${specializationNames}, online training, gym trainer`} />
                <meta property="og:title" content={`${trainerName} - Certified Fitness Trainer`} />
                <meta property="og:description" content={trainerBio || `Professional fitness trainer with ${trainerExperience} years of experience`} />
                <meta property="og:image" content={trainer.photo || placeimage} />
                <meta property="og:type" content="profile" />
                <link rel="canonical" href={`https://meragym.com/trainer/${slug}`} />
            </Helmet>

            <div className="trainer-public-profile">
                {/* Hero Section */}
                <section className="trainer-hero">
                    <div
                        className="hero-background"
                        style={{ backgroundImage: `url(${trainer.coverPhoto || ""})` }}
                    >
                        <div className="hero-overlay"></div>
                    </div>
                    <div className="container">
                        <div className="hero-content">
                            <div className="trainer-profile-card">
                                <div className="profile-image-wrapper">
                                    <img
                                        src={trainer.photo || placeimage}
                                        alt={trainerName}
                                        className="trainer-profile-image"
                                    />
                                    {trainer.is_certified_professional && (
                                        <div className="verified-badge">
                                            <i className="bi bi-patch-check-fill"></i>
                                        </div>
                                    )}
                                </div>
                                <div className="profile-info">
                                    <h1 className="trainer-name-hero">{trainerName}</h1>
                                    <p className="trainer-title">
                                        Certified Fitness Trainer
                                        {trainer.city && ` • ${trainer.city}`}
                                    </p>
                                    <div className="trainer-stats">
                                        {trainerExperience > 0 && (
                                            <div className="stat-item">
                                                <i className="bi bi-award-fill"></i>
                                                <span>{trainerExperience}+ Years</span>
                                            </div>
                                        )}
                                        <div className="stat-item">
                                            <i className="bi bi-people-fill"></i>
                                            <span>Active Trainer</span>
                                        </div>
                                        {(trainer.skills || []).length > 0 && (
                                            <div className="stat-item">
                                                <i className="bi bi-trophy-fill"></i>
                                                <span>{trainer.skills.length} Skills</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="profile-actions">
                                        <a
                                            href={`https://wa.me/?text=Hi, I'd like to book a session with ${encodeURIComponent(trainerName)} on MeraGym`}
                                            className="btn btn-danger btn-lg"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <i className="bi bi-whatsapp me-2"></i>
                                            Book via WhatsApp
                                        </a>
                                        <a
                                            href={`mailto:${trainer.email || ""}`}
                                            className="btn btn-outline-light btn-lg"
                                        >
                                            <i className="bi bi-envelope me-2"></i>
                                            Contact
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Navigation Tabs */}
                <section className="profile-navigation">
                    <div className="container">
                        <div className="nav-tabs-wrapper">
                            <button
                                className={`nav-tab ${activeTab === "about" ? "active" : ""}`}
                                onClick={() => setActiveTab("about")}
                            >
                                <i className="bi bi-person me-2"></i>About
                            </button>
                            <button
                                className={`nav-tab ${activeTab === "services" ? "active" : ""}`}
                                onClick={() => setActiveTab("services")}
                            >
                                <i className="bi bi-cash-stack me-2"></i>Services & Pricing
                            </button>
                            {(trainer.gallery || []).length > 0 && (
                                <button
                                    className={`nav-tab ${activeTab === "gallery" ? "active" : ""}`}
                                    onClick={() => setActiveTab("gallery")}
                                >
                                    <i className="bi bi-images me-2"></i>Gallery
                                </button>
                            )}
                        </div>
                    </div>
                </section>

                {/* Content */}
                <section className="profile-content">
                    <div className="container">
                        <div className="row">
                            {/* Main Content */}
                            <div className="col-lg-8">

                                {/* About Tab */}
                                {activeTab === "about" && (
                                    <div className="content-section">
                                        {/* Bio */}
                                        {trainerBio && (
                                            <div className="section-card">
                                                <h3 className="section-title">
                                                    <i className="bi bi-info-circle me-2"></i>About Me
                                                </h3>
                                                <p className="bio-text">{trainerBio}</p>
                                            </div>
                                        )}

                                        {/* Specializations */}
                                        {(trainer.specializations || []).length > 0 && (
                                            <div className="section-card">
                                                <h3 className="section-title">
                                                    <i className="bi bi-trophy me-2"></i>Specializations
                                                </h3>
                                                <div className="specializations-grid">
                                                    {trainer.specializations.map((spec, index) => (
                                                        <div key={index} className="specialization-badge">
                                                            <i className={spec.icon || "bi bi-check-circle"}></i>
                                                            <span>{spec.name}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Skills */}
                                        {(trainer.skills || []).length > 0 && (
                                            <div className="section-card">
                                                <h3 className="section-title">
                                                    <i className="bi bi-lightning-fill me-2"></i>Skills
                                                </h3>
                                                <div className="skills-wrap">
                                                    {trainer.skills.map((skill, index) => (
                                                        <span key={index} className="skill-tag">{skill.name}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Certifications */}
                                        {(trainer.certifications || []).length > 0 && (
                                            <div className="section-card">
                                                <h3 className="section-title">
                                                    <i className="bi bi-patch-check me-2"></i>Certifications
                                                </h3>
                                                <div className="certifications-list-public">
                                                    {trainer.certifications.map((cert, index) => (
                                                        <div key={index} className="certification-item-public">
                                                            <i className="bi bi-award-fill"></i>
                                                            <div>
                                                                <h6>{cert.name}</h6>
                                                                <p>{cert.issuedBy}{cert.year ? ` • ${cert.year}` : ""}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Services Tab */}
                                {activeTab === "services" && (
                                    <div className="content-section">
                                        {(trainer.services || []).length > 0 ? (
                                            <div className="services-grid-public">
                                                {trainer.services.map((service, index) => (
                                                    <div key={index} className="service-card-public">
                                                        <div className="service-header-public">
                                                            <h4>{service.name}</h4>
                                                            <div className="service-price-public">
                                                                <span className="price-formatted">{service.price}</span>
                                                                <span className="duration-tag">{service.duration}</span>
                                                            </div>
                                                        </div>
                                                        {service.description && (
                                                            <p className="service-description-public">{service.description}</p>
                                                        )}
                                                        {(service.features || []).length > 0 && (
                                                            <ul className="service-features-public">
                                                                {service.features.map((feature, idx) => (
                                                                    <li key={idx}>
                                                                        <i className="bi bi-check-circle-fill"></i>
                                                                        {feature}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                        <a
                                                            href={`https://wa.me/?text=Hi, I'm interested in your "${encodeURIComponent(service.name)}" service on MeraGym`}
                                                            className="btn btn-danger w-100 mt-3"
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            <i className="bi bi-whatsapp me-2"></i>Enquire via WhatsApp
                                                        </a>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="empty-state-public">
                                                <i className="bi bi-cash-coin"></i>
                                                <p>No services listed yet</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Gallery Tab */}
                                {activeTab === "gallery" && (
                                    <div className="content-section">
                                        {(trainer.gallery || []).length > 0 ? (
                                            <div className="gallery-grid-public">
                                                {trainer.gallery.map((item, index) => (
                                                    <div key={index} className="gallery-item-public">
                                                        <img src={item.image} alt={`${trainerName} gallery ${index + 1}`} />
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="empty-state-public">
                                                <i className="bi bi-images"></i>
                                                <p>No photos available yet</p>
                                            </div>
                                        )}

                                        {(trainer.videos || []).length > 0 && (
                                            <div className="videos-section-public mt-4">
                                                <h3 className="section-title">Training Videos</h3>
                                                <div className="videos-grid-public">
                                                    {trainer.videos.map((video, index) => {
                                                        const ytMatch = typeof video.url === "string" && video.url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
                                                        const vimeoMatch = typeof video.url === "string" && video.url.match(/vimeo\.com\/(\d+)/);
                                                        const embedSrc = ytMatch
                                                            ? `https://www.youtube.com/embed/${ytMatch[1]}`
                                                            : vimeoMatch
                                                            ? `https://player.vimeo.com/video/${vimeoMatch[1]}`
                                                            : null;
                                                        if (!embedSrc) return null;
                                                        return (
                                                            <div key={index} className="video-item-public">
                                                                <iframe
                                                                    src={embedSrc}
                                                                    title={`Training video ${index + 1}`}
                                                                    frameBorder="0"
                                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                                    allowFullScreen
                                                                ></iframe>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Sidebar */}
                            <div className="col-lg-4">
                                {/* Quick Info Card */}
                                <div className="sidebar-card">
                                    <h4 className="sidebar-title">Quick Info</h4>
                                    <div className="quick-info-list">
                                        {trainerExperience > 0 && (
                                            <div className="info-row">
                                                <i className="bi bi-award"></i>
                                                <div>
                                                    <strong>Experience</strong>
                                                    <p>{trainerExperience}+ Years</p>
                                                </div>
                                            </div>
                                        )}
                                        {trainer.city && (
                                            <div className="info-row">
                                                <i className="bi bi-geo-alt"></i>
                                                <div>
                                                    <strong>Location</strong>
                                                    <p>{trainer.city}{trainer.country ? `, ${trainer.country}` : ", India"}</p>
                                                </div>
                                            </div>
                                        )}
                                        {(trainer.languages || []).length > 0 && (
                                            <div className="info-row">
                                                <i className="bi bi-translate"></i>
                                                <div>
                                                    <strong>Languages</strong>
                                                    <p>{Array.isArray(trainer.languages) ? trainer.languages.join(", ") : trainer.languages}</p>
                                                </div>
                                            </div>
                                        )}
                                        {trainer.availability_mode && (
                                            <div className="info-row">
                                                <i className="bi bi-display"></i>
                                                <div>
                                                    <strong>Training Mode</strong>
                                                    <p className="text-capitalize">{trainer.availability_mode}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Availability */}
                                {(trainer.availabilities || []).length > 0 && (
                                    <div className="sidebar-card">
                                        <h4 className="sidebar-title">Availability</h4>
                                        <div className="availability-list">
                                            {trainer.availabilities.map((slot, index) => (
                                                <div key={index} className="availability-item">
                                                    <strong>{slot.day}</strong>
                                                    <span>{slot.startTime} – {slot.endTime}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Contact Card */}
                                <div className="sidebar-card contact-card">
                                    <h4 className="sidebar-title">Get in Touch</h4>
                                    <a
                                        href={`https://wa.me/?text=Hi ${encodeURIComponent(trainerName)}, I found your profile on MeraGym and would like to book a session.`}
                                        className="btn btn-danger w-100 mb-2"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <i className="bi bi-whatsapp me-2"></i>Book via WhatsApp
                                    </a>
                                    {trainer.email && (
                                        <a href={`mailto:${trainer.email}`} className="btn btn-outline-danger w-100">
                                            <i className="bi bi-envelope me-2"></i>Send Email
                                        </a>
                                    )}
                                </div>

                                {/* Social Links */}
                                {(trainer.instagram || trainer.facebook || trainer.linkedin) && (
                                    <div className="sidebar-card">
                                        <h4 className="sidebar-title">Follow</h4>
                                        <div className="share-buttons">
                                            {trainer.instagram && (
                                                <a href={trainer.instagram} className="share-btn instagram" target="_blank" rel="noreferrer" aria-label="Instagram">
                                                    <i className="bi bi-instagram"></i>
                                                </a>
                                            )}
                                            {trainer.facebook && (
                                                <a href={trainer.facebook} className="share-btn facebook" target="_blank" rel="noreferrer" aria-label="Facebook">
                                                    <i className="bi bi-facebook"></i>
                                                </a>
                                            )}
                                            {trainer.linkedin && (
                                                <a href={trainer.linkedin} className="share-btn linkedin" target="_blank" rel="noreferrer" aria-label="LinkedIn">
                                                    <i className="bi bi-linkedin"></i>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
}
